import { Device, iPointTranslator, Vector2, Vector3 } from "@app/shared";
import { mat4, vec3 } from "gl-matrix";
import { Matrix, Vector } from "ts-matrix";

export class HomographyTranslator implements iPointTranslator {
    srcPts: Array<number> = [];
    dstPts: Array<number> = [];
    coeffs: Array<number> = [];
    coeffsInv: Array<number> = [];
    private height: number = 0;
    private rotation!: mat4;
    private invRotation!: mat4;
    private is2dMode: boolean = true;

    //Dot product of a matrix and vector
    dotMV(x: Matrix, y: Vector) {
        var p = x.rows,
            i;
        var ret = new Vector();

        for (i = 0; i < p; i++) {
            var row = new Vector(x.values[i]);
            ret.values[i] = y.dot(row);
        }
        return ret;
    }

    imageToGround(uv: Vector2): Vector3 {
        var ret = new Vector3();
        ret.x = (this.coeffs[0] * uv.x + this.coeffs[1] * uv.y + this.coeffs[2]) / (this.coeffs[6] * uv.x + this.coeffs[7] * uv.y + 1);
        ret.y = (this.coeffs[3] * uv.x + this.coeffs[4] * uv.y + this.coeffs[5]) / (this.coeffs[6] * uv.x + this.coeffs[7] * uv.y + 1);
        ret.z = 0;
        return ret;
    }

    imageToRadar(uv: Vector2): Vector3 {
        return this.groundToRadar(this.imageToGround(uv));
    }

    groundToImage(groundCartesian: Vector3): Vector2 {
        var ret = new Vector2();
        ret.x = (this.coeffsInv[0] * groundCartesian.x + this.coeffsInv[1] * groundCartesian.y + this.coeffsInv[2]) / (this.coeffsInv[6] * groundCartesian.x + this.coeffsInv[7] * groundCartesian.y + 1);
        ret.y = (this.coeffsInv[3] * groundCartesian.x + this.coeffsInv[4] * groundCartesian.y + this.coeffsInv[5]) / (this.coeffsInv[6] * groundCartesian.x + this.coeffsInv[7] * groundCartesian.y + 1);
        return ret;
    }

    groundToRadar(groundCartesian: Vector3): Vector3 {
        const translated = vec3.fromValues(groundCartesian.x, groundCartesian.y, groundCartesian.z - this.height);
        const rotated = vec3.transformMat4(vec3.create(), translated, this.rotation);
        return new Vector3({ x: rotated[0], y: rotated[1], z: rotated[2] });
    }

    radarToGround(radarCartesian: Vector3): Vector3 {
        const input = vec3.fromValues(radarCartesian.x, radarCartesian.y, radarCartesian.z);
        const rotated = vec3.transformMat4(vec3.create(), input, this.invRotation);
        const translated = vec3.fromValues(rotated[0], rotated[1], rotated[2] - this.height);
        return new Vector3({ x: translated[0], y: translated[1], z: translated[2] });
    }

    radarToImage(radarCartesian: Vector3): Vector2 {
        return this.groundToImage(this.radarToGround(radarCartesian));
    }

    getNormalizationCoefficients(srcPts: Array<number>, dstPts: Array<number>, isInverse: boolean) {
        if (isInverse) {
            var tmp = dstPts;
            dstPts = srcPts;
            srcPts = tmp;
        }

        var matA = new Matrix(8, 8, [
            [srcPts[0], srcPts[1], 1, 0, 0, 0, -1 * dstPts[0] * srcPts[0], -1 * dstPts[0] * srcPts[1]],
            [0, 0, 0, srcPts[0], srcPts[1], 1, -1 * dstPts[1] * srcPts[0], -1 * dstPts[1] * srcPts[1]],
            [srcPts[2], srcPts[3], 1, 0, 0, 0, -1 * dstPts[2] * srcPts[2], -1 * dstPts[2] * srcPts[3]],
            [0, 0, 0, srcPts[2], srcPts[3], 1, -1 * dstPts[3] * srcPts[2], -1 * dstPts[3] * srcPts[3]],
            [srcPts[4], srcPts[5], 1, 0, 0, 0, -1 * dstPts[4] * srcPts[4], -1 * dstPts[4] * srcPts[5]],
            [0, 0, 0, srcPts[4], srcPts[5], 1, -1 * dstPts[5] * srcPts[4], -1 * dstPts[5] * srcPts[5]],
            [srcPts[6], srcPts[7], 1, 0, 0, 0, -1 * dstPts[6] * srcPts[6], -1 * dstPts[6] * srcPts[7]],
            [0, 0, 0, srcPts[6], srcPts[7], 1, -1 * dstPts[7] * srcPts[6], -1 * dstPts[7] * srcPts[7]],
        ]);

        var matC = matA.transpose().multiply(matA).inverse();
        var matD = matC.multiply(matA.transpose());
        var matX = this.dotMV(matD, new Vector(dstPts));
        matX.values[8] = 1;
        return matX.values;
    }

    calculateFromParameters(is2d: boolean, height: number, pitchDeg: number, rollDeg: number, estimatedReturnHeight: number): boolean {
        return false;
    }

    calculateFromSettings(settings: Device | null, pitchDeg: number, rollDeg: number, estimatedReturnHeight: number): boolean {
        this.srcPts = settings?.homographySourcePoints?.flatMap((v) => [v.x, v.y])?.slice(0, 8) ?? [];
        this.dstPts = settings?.homographyDestinationPoints?.flatMap((v) => [v.x, v.y])?.slice(0, 8) ?? [];
        this.coeffs = this.getNormalizationCoefficients(this.srcPts, this.dstPts, false);
        this.coeffsInv = this.getNormalizationCoefficients(this.srcPts, this.dstPts, true);
        const orientation = { pitch: pitchDeg, roll: rollDeg };
        this.height = settings?.height ?? 5;

        var rotY: mat4 = mat4.fromYRotation(mat4.create(), (orientation.roll * Math.PI) / 180);
        var rotX = mat4.fromXRotation(mat4.create(), (orientation.pitch * Math.PI) / 180);
        this.rotation = mat4.multiply(mat4.create(), rotX, rotY);

        var rotY: mat4 = mat4.fromYRotation(mat4.create(), (-orientation.roll * Math.PI) / 180);
        var rotX = mat4.fromXRotation(mat4.create(), (-orientation.pitch * Math.PI) / 180);
        this.invRotation = mat4.multiply(mat4.create(), rotX, rotY);

        if (this.srcPts.length !== 8 || this.dstPts.length !== 8) {
            console.log("Invalid length for source or destination points, cannot calculate homography.");
            return false;
        }

        return true;
    }
}
